import cx from 'classnames'
import { Translate } from 'react-localize-redux'
import { ReactComponent as GoAqsLogo } from 'icons/GoAqsLogo.svg'
import { ReactComponent as GoIaqsMeterBase } from 'icons/GoIaqsMeter.svg'
import GoIaqsMarker from './GoIaqsMarker'
import styles from './GoIaqsInfoPanel.module.scss'
import { getGoIaqsCategory } from './IndicatorsSection.utils'

const POLLUTANT_DISPLAY_NAMES = {
  co2: 'CO₂',
  pm25: 'PM₂.₅'
}

const formatPollutantNames = pollutants => {
  if (!pollutants || pollutants.length === 0) return '—'
  return pollutants.map(p => POLLUTANT_DISPLAY_NAMES[p] || p).join(', ')
}

const METER_NEEDLE_ANGLE_BY_CATEGORY = {
  // The base SVG contains the full meter artwork. We hide its needle
  // in CSS and rotate a reusable needle around the dial center instead.
  Good: -60,
  Moderate: 0,
  Unhealthy: 60
}

const COLOR_BY_CATEGORY = {
  Good: '#6285C3',
  Moderate: '#F8AB12',
  Unhealthy: '#E52421'
}

const LETTER_BY_CATEGORY = {
  Good: 'A',
  Moderate: 'B',
  Unhealthy: 'Z'
}

// center = x-offset (px) of each score label within the 189px score bar, from Figma.
const SCORE_ITEMS = [
  { score: 10, category: 'Good', center: 13.51 },
  { score: 9, category: 'Good', center: 34.66 },
  { score: 8, category: 'Good', center: 53.01 },
  { score: 7, category: 'Moderate', center: 69.61 },
  { score: 6, category: 'Moderate', center: 85.43 },
  { score: 5, category: 'Moderate', center: 101.13 },
  { score: 4, category: 'Moderate', center: 116.84 },
  { score: 3, category: 'Unhealthy', center: 131.79 },
  { score: 2, category: 'Unhealthy', center: 147.67 },
  { score: 1, category: 'Unhealthy', center: 162.75 },
  { score: 0, category: 'Unhealthy', center: 178.76 }
].map(item => ({
  ...item,
  color: COLOR_BY_CATEGORY[item.category]
}))

const SCORE_SEGMENTS = [
  { category: 'Good', width: 61.73 },
  { category: 'Moderate', width: 63.64 },
  { category: 'Unhealthy', width: 63.64 }
].map(item => ({
  ...item,
  color: COLOR_BY_CATEGORY[item.category]
}))

const getScoreItem = score => SCORE_ITEMS.find(item => item.score === score)

const GoIaqsMeterNeedle = ({ angle }) => (
  // The rotation origin is the meter hub center from the Figma SVG. Rotating
  // the path around this point preserves the same needle length and pivot.
  <svg
    viewBox='0 0 137.81 79.6275'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    className={styles.meterNeedle}
    aria-hidden='true'
    focusable='false'
  >
    <path
      transform={`rotate(${angle} 68.9033 68.8962)`}
      d='M76.0468 64.2001C74.9992 62.4544 73.416 61.256 71.6463 60.6701L69.2464 29.4414C69.2227 29.1574 68.8055 29.1603 68.7877 29.4414L66.734 60.605C65.7308 60.9098 64.769 61.4187 63.899 62.1407C60.8983 64.6351 60.1201 69.0173 62.0761 72.3934C64.5797 76.7165 70.3147 77.7817 74.2031 74.6393C77.3104 72.1271 78.1034 67.6236 76.0468 64.1972V64.2001ZM65.8403 70.5855C64.6092 68.7894 65.0679 66.3335 66.8642 65.1026C68.6605 63.8717 71.1166 64.3303 72.3477 66.1264C73.5787 67.9225 73.1201 70.3784 71.3238 71.6093C69.5275 72.8402 67.0713 72.3816 65.8403 70.5855Z'
      fill='#143D65'
    />
  </svg>
)

const GoIaqsMeter = ({ category }) => (
  <div className={styles.meterImage}>
    {/* Dial art without its original needle; the overlay needle below owns state. */}
    <GoIaqsMeterBase
      className={styles.meterBase}
      aria-hidden='true'
      focusable='false'
    />
    <GoIaqsMeterNeedle angle={METER_NEEDLE_ANGLE_BY_CATEGORY[category]} />
  </div>
)

const ScoreBar = ({ value }) => {
  const score = value
  const scoreItem = getScoreItem(score)

  return (
    <div className={styles.scoreBarWrapper}>
      <div
        className={styles.scoreBarBase}
        aria-hidden='true'
      >
        {SCORE_SEGMENTS.map(item => (
          <span
            key={item.category}
            className={styles.scoreBarSegment}
            style={{
              backgroundColor: item.color,
              width: `${item.width}px`
            }}
          />
        ))}
        {SCORE_ITEMS.map(item => (
          <span
            key={item.score}
            className={styles.scoreBarValue}
            style={{ left: `${item.center}px` }}
          >
            {item.score}
          </span>
        ))}
      </div>
      <GoIaqsMarker
        color={scoreItem.color}
        letter={LETTER_BY_CATEGORY[scoreItem.category]}
        score={score}
        aria-hidden='true'
        focusable='false'
        className={styles.scoreBarMarker}
        style={{ left: `${scoreItem.center}px` }}
      />
    </div>
  )
}

const GoIaqsInfoPanel = ({ value, dominantPollutants, isModal = false, showTitle = true }) => {
  const hasValue = value != null
  const roundedValue = hasValue ? value : null
  const category = hasValue ? getGoIaqsCategory(roundedValue) : null

  return (
    <div className={cx(styles.panel, isModal && styles.modalPanel)}>
      {showTitle && (
        <h4 className={styles.title}>
          <Translate id='goIaqsIndicatorTitle' />
        </h4>
      )}
      {hasValue && <GoIaqsMeter category={category} />}
      <p className={styles.description}>
        <Translate
          id='goIaqsIndicatorInfo'
          options={{ renderInnerHtml: true }}
        />
      </p>
      {hasValue && (
        <>
          <ScoreBar value={roundedValue} />
          <div className={styles.scoreRow}>
            <span className={styles.scoreNumber}>{roundedValue}</span>
            <span className={styles.scoreLabel}>
              <Translate id={`goIaqs${category}`} />
            </span>
            <GoAqsLogo className={styles.logo} />
          </div>
          <div className={styles.infoSection}>
            <p className={styles.sectionLabel}>
              <Translate id='goIaqsAdviceLabel' />
            </p>
            <p className={styles.sectionText}>
              <Translate id={`goIaqsAdvice${category}`} />
            </p>
          </div>
          <div className={styles.infoSection}>
            <p className={styles.sectionLabel}>
              <Translate id='goIaqsDominantLabel' />
            </p>
            <p className={styles.sectionText}>{formatPollutantNames(dominantPollutants)}</p>
          </div>
        </>
      )}
    </div>
  )
}

export default GoIaqsInfoPanel
